/**
 * Catalogo de icones disponiveis para Times.
 *
 * O backend grava `teams.icon` como string livre. O frontend usa essa
 * string para escolher um componente do lucide. Manter o catalogo
 * pequeno e curado evita o user digitar um nome qualquer e ter um
 * fallback feio.
 */

import {
  Beaker,
  BookOpen,
  Briefcase,
  Building2,
  Code,
  Cog,
  GraduationCap,
  Hammer,
  Headphones,
  HeartPulse,
  LucideIcon,
  Megaphone,
  PenTool,
  Rocket,
  Scale,
  Shield,
  ShoppingCart,
  Sparkles,
  Stethoscope,
  TrendingUp,
  Truck,
  Users,
  Wallet,
} from "lucide-react";

export interface TeamIconDef {
  /** Nome canonico salvo no backend em `teams.icon`. */
  name: string;
  /** Componente lucide a renderizar. */
  Icon: LucideIcon;
  /** Tooltip / aria-label em pt-BR. */
  label: string;
}

export const TEAM_ICONS: TeamIconDef[] = [
  { name: "briefcase", Icon: Briefcase, label: "Negocios" },
  { name: "scale", Icon: Scale, label: "Juridico" },
  { name: "gavel", Icon: Hammer, label: "Juridico (martelo)" },
  { name: "shield", Icon: Shield, label: "Seguranca" },
  { name: "users", Icon: Users, label: "Pessoas" },
  { name: "rocket", Icon: Rocket, label: "Engenharia" },
  { name: "code", Icon: Code, label: "Desenvolvimento" },
  { name: "cog", Icon: Cog, label: "Operacoes" },
  { name: "headphones", Icon: Headphones, label: "Suporte" },
  { name: "wallet", Icon: Wallet, label: "Financeiro" },
  { name: "trending-up", Icon: TrendingUp, label: "Vendas" },
  { name: "megaphone", Icon: Megaphone, label: "Marketing" },
  { name: "truck", Icon: Truck, label: "Logistica" },
  { name: "shopping-cart", Icon: ShoppingCart, label: "Comercial" },
  { name: "building", Icon: Building2, label: "Corporativo" },
  { name: "sparkles", Icon: Sparkles, label: "Inovacao" },
  { name: "book", Icon: BookOpen, label: "Educacao" },
  { name: "graduation", Icon: GraduationCap, label: "Treinamento" },
  { name: "stethoscope", Icon: Stethoscope, label: "Saude" },
  { name: "heart", Icon: HeartPulse, label: "Bem-estar" },
  { name: "beaker", Icon: Beaker, label: "Pesquisa" },
  { name: "pen", Icon: PenTool, label: "Design" },
];

const ICON_BY_NAME = new Map(TEAM_ICONS.map((d) => [d.name, d]));

/**
 * Retorna o componente de icone correspondente ao nome salvo. Faz
 * fallback para ``Users`` caso o nome nao esteja no catalogo (ex.:
 * times legados).
 */
export function getTeamIcon(name: string | null | undefined): LucideIcon {
  if (!name) return Users;
  return ICON_BY_NAME.get(name)?.Icon ?? Users;
}

export function getTeamIconDef(
  name: string | null | undefined,
): TeamIconDef | null {
  if (!name) return null;
  return ICON_BY_NAME.get(name) ?? null;
}
